import React from 'react';
import { PriceRoundingRuleConfig } from '@retail/rounding/types';
import { ContainedButton, Dialog, OutlinedButton } from '@shared/components';
import { DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useAppTFunction } from '@retail/app/i18n';
import { useRoundingRuleActions } from '@retail/rounding/context';
import { WarningIcon } from '@shared/custom-icons';

interface Props {
  roundingRule: PriceRoundingRuleConfig;
  onClose: () => void;
}

export const DeleteRoundingRuleDialog: React.FC<Props> = ({ roundingRule, onClose }) => {
  const t = useAppTFunction();
  const { deleteRoundingRuleSet, isLoadingDeleteRuleSet, conditionInUse } =
    useRoundingRuleActions();
  const onSubmit = async () => {
    try {
      await deleteRoundingRuleSet(roundingRule.id);
      onClose();
    } catch (err: any) {
      if (err.status === 400) {
        conditionInUse.onOpen();
      }
    }
  };

  return conditionInUse.isOpen ? (
    <Dialog
      open={conditionInUse.isOpen}
      onClose={() => {
        conditionInUse.onClose();
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>{`${t('rounding.actions.deleteRule')} ${roundingRule.name}`}</DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="error" fontSize="large" />
        <Stack>
          <Typography variant="body1">{t('calculus.rounding.roundingRuleIsInUse')}</Typography>
          <Typography>{t('calculus.rounding.roundingRuleIsInUseDesc')}</Typography>{' '}
        </Stack>
      </DialogContent>
      <DialogActions>
        <ContainedButton size="small" onClick={onClose}>
          {t('common.close')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  ) : (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{`${t('rounding.actions.deleteRule')} ${roundingRule.name}`}</DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {t('rounding.deleteRule.description1')}
        </Typography>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} size="small" loading={isLoadingDeleteRuleSet}>
          {t('common.cancel')}
        </OutlinedButton>
        <ContainedButton
          onClick={onSubmit}
          size="small"
          loading={isLoadingDeleteRuleSet}
          color="error"
        >
          {t('rounding.actions.deleteRule')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
};
